import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../contexts/LocaleContext';
import useTourTeams from '../hooks/useTourTeams';
import {
  blankContacts, blankFixture, deleteFixture, fixtureMismatch, fmtFixtureDate,
  isFixturesActivity, kitClash, kitColourHex, kitPartText, localAgeLabel, saveFixture, sortFixtures,
} from '../utils/teamFixtures';

// ── Team fixtures UI ─────────────────────────────────────────────────────────
// The Fixtures section on the Team Info tab, the add/edit form it shares with
// the itinerary's Fixtures activity, and the match lines every itinerary view
// shows. See utils/teamFixtures.js for how the records fit together.

const ABILITIES = ['Low', 'Medium', 'High'];
const KIT_COLOURS = ['White', 'Black', 'Grey', 'Red', 'Blue', 'Yellow', 'Green', 'Orange', 'Navy', 'Maroon', 'Sky Blue', 'Purple', 'Pink'];

const inputCls = 'w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb';
const labelCls = 'block text-[11px] font-semibold text-[#6B7280] mb-1';

/** Live opposition/referee contacts for a tour's fixtures (staff + tour leaders only). */
export function useFixtureContacts(requestId, enabled) {
  const [byId, setById] = useState({});
  useEffect(() => {
    if (!enabled || !requestId) { setById({}); return undefined; }
    return onSnapshot(
      query(collection(db, 'fixtureContacts'), where('requestId', '==', requestId)),
      (snap) => setById(Object.fromEntries(snap.docs.map((d) => [d.id, d.data()]))),
      (err) => { console.warn('Fixture contacts unavailable', err); setById({}); }
    );
  }, [requestId, enabled]);
  return byId;
}

/** The saved Locations library, for picking a venue. */
function useLocations() {
  const [locations, setLocations] = useState([]);
  useEffect(() => onSnapshot(
    query(collection(db, 'locations'), orderBy('name')),
    (snap) => setLocations(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => setLocations([])
  ), []);
  return locations;
}

function PersonFields({ title, value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div>
      <p className="text-[11px] font-bold text-[#374151] mb-1.5">{title}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input className={inputCls} value={value.name} onChange={(e) => set('name', e.target.value)} placeholder="Name" />
        <input className={inputCls} type="tel" value={value.phone} onChange={(e) => set('phone', e.target.value)} placeholder="Phone" />
        <input className={inputCls} type="email" value={value.email} onChange={(e) => set('email', e.target.value)} placeholder="Email" />
      </div>
    </div>
  );
}

/**
 * Add or edit one fixture. Used from the Team Info tab and from the
 * itinerary's Fixtures activity — both save the same record.
 */
export function FixtureFormModal({ requestId, fixture = null, defaults = {}, teams, canSeeContacts, contacts = null, onSaved, onClose }) {
  const { currentUser } = useAuth();
  const locations = useLocations();
  const isEdit = !!fixture?.id;
  const [form, setForm] = useState(() => ({
    ...blankFixture(defaults),
    ...(fixture || {}),
    yobText: (fixture?.opponentYearsOfBirth || []).join(', '),
  }));
  const [people, setPeople] = useState(() => ({ ...blankContacts(), ...(contacts || {}) }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const selectedTeam = teams.find((t) => t.id === form.teamId) || null;

  function pickVenue(name) {
    const loc = locations.find((l) => l.name === name);
    setForm((p) => (loc
      ? { ...p, venueName: loc.name, venueAddress: loc.address || '', venueGoogleLink: loc.googleLink || '' }
      : { ...p, venueName: name }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.teamId) { setError('Choose which team is playing.'); return; }
    if (!form.date) { setError('Add the date.'); return; }
    setSaving(true);
    setError('');
    try {
      const opponentYearsOfBirth = form.yobText.split(/[\s,/]+/).map(Number).filter((y) => y > 1900 && y < 2100);
      const id = await saveFixture(requestId, fixture?.id || null, { ...form, opponentYearsOfBirth }, {
        contacts: canSeeContacts ? people : undefined,
        user: currentUser,
      });
      onSaved?.(id);
      onClose();
    } catch (err) {
      console.error(err);
      setError('Could not save the fixture. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4"
      style={{ background: 'rgba(13,34,64,0.4)', backdropFilter: 'blur(3px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-[14px] shadow-xl w-full max-w-[560px] max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-[#F1F3F6] flex items-center justify-between">
          <h3 className="text-[15px] font-bold text-navy">{isEdit ? 'Edit fixture' : 'Add fixture'}</h3>
          <button type="button" onClick={onClose} className="text-[#9CA3AF] hover:text-[#374151] text-[18px] leading-none">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className={labelCls}>Team playing *</label>
            {teams.length === 0 ? (
              <p className="text-[12px] text-[#9CA3AF]">No teams yet. Teams are created from the squads in Team Info, or on the Teams tab.</p>
            ) : (
              <select className={inputCls} value={form.teamId} onChange={(e) => set('teamId', e.target.value)}>
                <option value="">Select team…</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}{t.ageLabel ? ` (${t.ageLabel.split(' · born')[0]})` : ''}</option>
                ))}
              </select>
            )}
            {selectedTeam && (selectedTeam.kit ? (
              <div className="mt-2 px-3 py-2 rounded-[8px] bg-[#F9FAFB] border border-[#F1F3F6]">
                <p className="text-[10.5px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-0.5">Our kit (from Team Info)</p>
                <TeamKit kit={selectedTeam.kit} />
              </div>
            ) : (
              <p className="mt-1.5 text-[11.5px] text-[#9CA3AF]">
                {selectedTeam.squadId
                  ? 'No kit colours in Team Info for this squad yet.'
                  : 'This team isn’t linked to a Team Info squad, so its kit can’t be shown. Link it on the Teams tab.'}
              </p>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Date *</label>
              <input type="date" className={inputCls} value={form.date} onChange={(e) => set('date', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Kick-off</label>
              <input type="time" className={inputCls} value={form.time} onChange={(e) => set('time', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Ends</label>
              <input type="time" className={inputCls} value={form.endTime} onChange={(e) => set('endTime', e.target.value)} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Venue / pitch</label>
            <input
              className={inputCls}
              list={`fixture-venues-${requestId}`}
              value={form.venueName}
              onChange={(e) => pickVenue(e.target.value)}
              placeholder="Pick a saved location or type, e.g. Ciudad Deportiva, Pitch 2"
            />
            <datalist id={`fixture-venues-${requestId}`}>
              {locations.map((l) => <option key={l.id} value={l.name} />)}
            </datalist>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              <input className={inputCls} value={form.venueAddress} onChange={(e) => set('venueAddress', e.target.value)} placeholder="Address" />
              <input className={inputCls} type="url" value={form.venueGoogleLink} onChange={(e) => set('venueGoogleLink', e.target.value)} placeholder="Google Maps link" />
            </div>
          </div>

          <div className="pt-1 border-t border-[#F1F3F6]">
            <p className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider mt-3 mb-2">Opposition</p>
            <div className="space-y-2">
              <input className={inputCls} value={form.opponentName} onChange={(e) => set('opponentName', e.target.value)} placeholder="Team name, e.g. CD Altea Infantil A" />
              <div className="grid grid-cols-2 gap-2">
                <input className={inputCls} list="fixture-kit-colours" value={form.opponentPrimaryColour} onChange={(e) => set('opponentPrimaryColour', e.target.value)} placeholder="Kit colour" />
                <input className={inputCls} list="fixture-kit-colours" value={form.opponentSecondaryColour} onChange={(e) => set('opponentSecondaryColour', e.target.value)} placeholder="Second colour" />
                <datalist id="fixture-kit-colours">{KIT_COLOURS.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
              {kitClash(form, selectedTeam?.kit) && (
                <p className="text-[11.5px] font-semibold text-dr">👕 {kitClash(form, selectedTeam?.kit)}</p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <input className={inputCls} value={form.yobText} onChange={(e) => set('yobText', e.target.value)} placeholder="Years of birth, e.g. 2012, 2013" />
                <select className={inputCls} value={form.opponentAbility} onChange={(e) => set('opponentAbility', e.target.value)}>
                  <option value="">Ability…</option>
                  {ABILITIES.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
          </div>

          {canSeeContacts && (
            <div className="pt-1 border-t border-[#F1F3F6] space-y-3">
              <p className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider mt-3">
                Contacts <span className="normal-case font-semibold tracking-normal">· MST staff and tour leaders only</span>
              </p>
              <PersonFields title="Opposition coach" value={people.oppositionCoach} onChange={(v) => setPeople((p) => ({ ...p, oppositionCoach: v }))} />
              <PersonFields title="Referee" value={people.referee} onChange={(v) => setPeople((p) => ({ ...p, referee: v }))} />
            </div>
          )}

          <div>
            <label className={labelCls}>Notes</label>
            <input className={inputCls} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="e.g. Bring both kits, 11-a-side, 2 × 35 min" />
          </div>

          {error && <p className="text-[12px] text-dr">{error}</p>}
          <div className="flex gap-2">
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 bg-pb hover:bg-navy text-white text-[13px] font-bold rounded-[9px] transition disabled:opacity-60">
              {saving ? 'Saving…' : isEdit ? 'Save fixture' : 'Add fixture'}
            </button>
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-[13px] text-[#6B7280] hover:text-[#374151]">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Swatch({ colour }) {
  const hex = kitColourHex(colour);
  if (!hex) return null;
  return <span title={colour} className="inline-block w-2.5 h-2.5 rounded-full border border-[#D1D5DB] align-middle flex-shrink-0" style={{ background: hex }} />;
}

function KitPart({ label, part }) {
  const t = useT();
  if (!part) return null;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="font-semibold text-[#374151]">{label}</span>
      <Swatch colour={part.jersey} />
      {part.jersey2 && part.pattern !== 'plain' && <Swatch colour={part.jersey2} />}
      <span>{kitPartText(part, t)}</span>
    </span>
  );
}

/** Our team's home/away kit from Team Info, e.g. "Home ● Red shirt, Black shorts · Away ● White shirt…". */
export function TeamKit({ kit, size = 'md', className = '' }) {
  const t = useT();
  if (!kit) return null;
  const text = size === 'sm' ? 'text-[11px]' : 'text-[11.5px]';
  return (
    <p className={`${text} text-[#6B7280] leading-snug flex flex-wrap items-center gap-x-2.5 gap-y-0.5 ${className}`}>
      <KitPart label={t('fixtures.home')} part={kit.home} />
      <KitPart label={t('fixtures.away')} part={kit.away} />
      {!kit.away && kit.home && <span className="text-[#9CA3AF]">{t('fixtures.oneKitOnly')}</span>}
    </p>
  );
}

function ContactLine({ label, p }) {
  if (!p || !(p.name || p.phone || p.email)) return null;
  return (
    <p className="text-[11.5px] text-[#6B7280]">
      <span className="text-[#9CA3AF]">{label}: </span>
      <span className="font-semibold text-[#374151]">{p.name || '—'}</span>
      {p.phone && <> · <a href={`tel:${p.phone}`} className="text-pb hover:underline" onClick={(e) => e.stopPropagation()}>{p.phone}</a></>}
      {p.email && <> · <a href={`mailto:${p.email}`} className="text-pb hover:underline" onClick={(e) => e.stopPropagation()}>{p.email}</a></>}
    </p>
  );
}

/** One match: "U14s vs CD Altea · 10:00 · Pitch 1", plus contacts where allowed. */
export function MatchLine({ fixture: f, team, isMine = false, contacts = null, showDate = false, warning = '', size = 'md', onEdit = null }) {
  const t = useT();
  const kit = [f.opponentPrimaryColour, f.opponentSecondaryColour].filter(Boolean).map((c) => t.value('colour', c)).join('/');
  const clash = kitClash(f, team?.kit, t);
  const when = [showDate && fmtFixtureDate(f.date, undefined, t.dateLocale), f.time && `${f.time}${f.endTime ? `–${f.endTime}` : ''}`].filter(Boolean).join(' · ');
  const text = size === 'sm' ? 'text-[11.5px]' : 'text-[13px]';
  return (
    <div className={`rounded-[8px] px-2.5 py-1.5 ${isMine ? 'bg-[#FFFBEB] border border-[#FCD34D]' : 'bg-[#F9FAFB] border border-[#F1F3F6]'}`}>
      <div className="flex items-start gap-2">
        <span className={text}>⚽</span>
        <div className="flex-1 min-w-0">
          <p className={`${text} font-semibold text-[#111827] leading-snug`}>
            {team?.name || t('fixtures.teamRemoved')}
            {team?.ageLabel && <span className="font-normal text-[#6B7280]"> ({localAgeLabel(team.ageLabel.split(' · born')[0], t)})</span>}
            <span className="font-normal text-[#9CA3AF]">{t('fixtures.vs')}</span>
            {f.opponentName || t('fixtures.oppTbc')}
            {kit && <span className="font-normal text-[#6B7280]"> ({kit})</span>}
            {isMine && <span className="ml-1.5 align-middle px-1.5 py-[1px] rounded-full bg-[#F59E0B] text-white text-[10px] font-bold">{t('fixtures.yourTeam')}</span>}
          </p>
          <TeamKit kit={team?.kit} size={size} />
          <p className="text-[11.5px] text-[#6B7280] leading-snug">
            {when}
            {when && f.venueName && ' · '}
            {f.venueName && (f.venueGoogleLink
              ? <a href={f.venueGoogleLink} target="_blank" rel="noopener noreferrer" className="text-pb hover:underline" onClick={(e) => e.stopPropagation()}>📍 {f.venueName}</a>
              : <>📍 {f.venueName}</>)}
          </p>
          {f.notes && <p className="text-[11.5px] text-[#9CA3AF] italic">{f.notes}</p>}
          {contacts && (
            <>
              <ContactLine label={t('fixtures.oppCoach')} p={contacts.oppositionCoach} />
              <ContactLine label={t('fixtures.referee')} p={contacts.referee} />
            </>
          )}
          {clash && <p className="text-[11px] font-semibold text-dr">👕 {clash}</p>}
          {warning && <p className="text-[11px] font-semibold text-[#D97706]">⚠ {warning}</p>}
        </div>
        {onEdit && (
          <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(f); }} className="text-[11px] font-semibold text-pb hover:text-navy flex-shrink-0">Edit</button>
        )}
      </div>
    </div>
  );
}

/**
 * The matches on one itinerary activity. Missing fixtures (deleted from Team
 * Info) show as "Fixture removed" when `showRemoved`, otherwise are skipped.
 */
export function ActivityMatches({ item, fixturesById, teamsById, contactsById = null, myLeaderId = null, departureDate, showRemoved = false, showWarnings = false, size = 'md', onEdit = null }) {
  const t = useT();
  const ids = item?.fixtureIds || [];
  if (!isFixturesActivity(item?.activityType) || ids.length === 0) return null;
  const present = sortFixtures(ids.map((id) => fixturesById[id]).filter(Boolean), teamsById);
  const removed = ids.filter((id) => !fixturesById[id]).length;
  if (present.length === 0 && !(showRemoved && removed)) return null;
  return (
    <div className="space-y-1 mt-1.5">
      <p className="text-[10.5px] font-bold text-[#9CA3AF] uppercase tracking-wider">
        {present.length === 1 ? t('fixtures.matchOne') : t('fixtures.matchMany', { n: present.length })}
      </p>
      {present.map((f) => {
        const team = teamsById[f.teamId];
        return (
          <MatchLine
            key={f.id}
            fixture={f}
            team={team}
            isMine={!!myLeaderId && team?.tourLeaderId === myLeaderId}
            contacts={contactsById ? contactsById[f.id] : null}
            warning={showWarnings ? fixtureMismatch(f, item, departureDate) : ''}
            size={size}
            onEdit={onEdit}
          />
        );
      })}
      {showRemoved && removed > 0 && (
        <p className="text-[11px] font-semibold text-dr">
          {removed} fixture{removed !== 1 ? 's' : ''} removed from Team Info. Edit this activity to take {removed !== 1 ? 'them' : 'it'} off.
        </p>
      )}
    </div>
  );
}

/**
 * The Fixtures section of the Team Info tab: each team with its matches.
 * `canEdit` for staff and tour leaders (who also see the contacts);
 * `showContacts` shows the contacts read-only (football operations);
 * `showItineraryLinks` false drops the "on the itinerary" line for viewers
 * who can't see the itinerary.
 */
export default function TeamFixturesSection({ requestId, request, canEdit = false, showContacts = canEdit, showItineraryLinks = true, myLeaderId = null }) {
  const { teams, teamsById, fixtures, loading } = useTourTeams(requestId, request);
  const contactsById = useFixtureContacts(requestId, showContacts);
  const t = useT();
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null); // { fixture, defaults }
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    if (!showItineraryLinks) return undefined;
    return onSnapshot(
      collection(db, 'requests', requestId, 'itinerary'),
      (snap) => setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setItems([])
    );
  }, [requestId, showItineraryLinks]);

  // Where each fixture sits on the itinerary.
  const onItinerary = useMemo(() => {
    const map = {};
    for (const it of items) {
      for (const fid of it.fixtureIds || []) (map[fid] = map[fid] || []).push(it);
    }
    return map;
  }, [items]);

  const byTeam = useMemo(() => {
    const map = {};
    for (const f of sortFixtures(fixtures, teamsById)) (map[f.teamId] = map[f.teamId] || []).push(f);
    return map;
  }, [fixtures, teamsById]);
  const orphaned = fixtures.filter((f) => !teamsById[f.teamId]);
  const ordered = myLeaderId
    ? [...teams.filter((t) => t.tourLeaderId === myLeaderId), ...teams.filter((t) => t.tourLeaderId !== myLeaderId)]
    : teams;

  if (loading) return null;

  const renderFixture = (f, team) => {
    const where = onItinerary[f.id] || [];
    return (
      <div key={f.id}>
        <MatchLine
          fixture={f}
          team={team}
          isMine={!!myLeaderId && team?.tourLeaderId === myLeaderId}
          contacts={showContacts ? contactsById[f.id] : null}
          showDate
          onEdit={canEdit ? (fx) => setModal({ fixture: fx }) : null}
        />
        <div className="flex items-center gap-3 px-1 mt-1">
          {showItineraryLinks && <p className="text-[11px] text-[#9CA3AF] flex-1">
            {where.length
              ? <>{t('fixtures.onItinerary', { days: where.map((it) => `${t('fixtures.dayN', { n: it.day })}${it.time ? ` ${it.time}` : ''}`).join(', ') })}</>
              : <span className="text-[#D97706]">{t('fixtures.notOnItinerary')}</span>}
          </p>}
          {canEdit && (confirmDelete === f.id ? (
            <span className="flex items-center gap-2">
              <button type="button" onClick={() => { deleteFixture(requestId, f.id); setConfirmDelete(null); }} className="text-[11px] font-bold text-dr">Delete</button>
              <button type="button" onClick={() => setConfirmDelete(null)} className="text-[11px] font-semibold text-[#9CA3AF]">Keep</button>
            </span>
          ) : (
            <button type="button" onClick={() => setConfirmDelete(f.id)} className="text-[11px] font-semibold text-[#9CA3AF] hover:text-dr">Delete</button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
      <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
        <div>
          <h3 className="text-[14.5px] font-bold text-[#111827]">{t('fixtures.title')}</h3>
          <p className="text-[12px] text-[#9CA3AF]">{fixtures.length === 1 ? t('fixtures.countOne') : t('fixtures.countMany', { n: fixtures.length })}</p>
        </div>
        {canEdit && teams.length > 0 && (
          <button type="button" onClick={() => setModal({ fixture: null, defaults: {} })}
            className="px-3 py-1.5 text-[12px] font-bold text-white bg-pb hover:bg-navy rounded-[8px] transition">
            + Add fixture
          </button>
        )}
      </div>
      <div className="px-[18px] py-[16px] space-y-5">
        {teams.length === 0 ? (
          <p className="text-[13px] text-[#9CA3AF]">{t('fixtures.noTeams')}</p>
        ) : ordered.map((team) => {
          const list = byTeam[team.id] || [];
          const isMine = !!myLeaderId && team.tourLeaderId === myLeaderId;
          return (
            <div key={team.id}>
              <div className="flex items-center gap-2 mb-2">
                <p className="text-[13px] font-bold text-[#111827]">{team.name}</p>
                {isMine && <span className="px-2 py-[2px] rounded-full bg-[#F59E0B] text-white text-[10.5px] font-bold">{t('fixtures.yourTeam')}</span>}
                {team.ageLabel && <p className="text-[12px] text-[#6B7280]">{localAgeLabel(team.ageLabel, t)}</p>}
                {team.tourLeaderName && <p className="text-[11.5px] text-[#9CA3AF]">{t('fixtures.tourLeader', { name: team.tourLeaderName })}</p>}
                {canEdit && (
                  <button type="button" onClick={() => setModal({ fixture: null, defaults: { teamId: team.id } })}
                    className="ml-auto text-[11.5px] font-semibold text-pb hover:text-navy">+ Fixture</button>
                )}
              </div>
              {list.length === 0
                ? <p className="text-[12px] text-[#D1D5DB]">{t('fixtures.noFixtures')}</p>
                : <div className="space-y-2.5">{list.map((f) => renderFixture(f, team))}</div>}
            </div>
          );
        })}
        {orphaned.length > 0 && (
          <div>
            <p className="text-[13px] font-bold text-dr mb-2">{t('fixtures.teamRemoved')}</p>
            <div className="space-y-2.5">{orphaned.map((f) => renderFixture(f, null))}</div>
          </div>
        )}
      </div>

      {modal && (
        <FixtureFormModal
          requestId={requestId}
          fixture={modal.fixture}
          defaults={modal.defaults}
          teams={teams}
          canSeeContacts={canEdit}
          contacts={modal.fixture ? contactsById[modal.fixture.id] : null}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

